/**
 * A small sample matter + naive value formatter for Admin -> PDF Layout's live preview,
 * mirroring functions/index.js's PDF_PREVIEW_FIXTURE_MATTER (same shape, same values)
 * used by the "Preview PDF" button's server-side render — so the fast CSS preview and
 * the on-demand real PDF show recognizably the same content, not two unrelated fixtures.
 *
 * Deliberately naive (no directory lookups for practice-group/people selects, no
 * money/date locale formatting beyond the basics) — this is a fast approximation for
 * "does this look roughly right while I'm dragging things around," not a replacement for
 * the real renderer. The exact, fully-formatted output is always one "Preview PDF" click
 * away (functions/src/documents/renderFieldValue.js does the real formatting server-side).
 */

const SAMPLE_MATTER = {
  client: { name: 'Sample Client, Inc.', contactName: 'Jordan Contact', isFlatFee: false },
  header: { date: new Date().toISOString().slice(0, 10), isNewClient: true },
  representation: { description: 'General corporate advice.' },
  clientSource: { source: 'marketing_efforts' },
  matterType: { isLitigation: false, isNonLitigation: true },
  nonLitigation: { handledBefore: true },
  fees: { estimatedTotalFees: 15000 },
  areasOfLaw: 'corporate_llc',
  wallBuilder: { practiceGroupIds: [], personUids: [], notes: 'Sample wall designation notes.' },
  filesToBeOpened: { matterName: 'Sample Matter — General Corporate' },
  origination: { responsiblePartnerUid: null, workingAttorneys: [] },
};

function getPath(obj, path) {
  if (!path) return undefined;
  return String(path)
    .split('.')
    .reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), obj);
}

/** A short, plausible-looking placeholder for a field this sample matter has no answer for. */
function placeholderFor(field) {
  return `(${field.label})`;
}

export function sampleValueFor(field) {
  const raw = getPath(SAMPLE_MATTER, field.path);
  if (raw === undefined || raw === null || raw === '') return placeholderFor(field);
  if (field.type === 'yesno') return raw === true ? 'Yes' : raw === false ? 'No' : placeholderFor(field);
  if (field.type === 'checkbox') return raw === true ? 'Yes' : placeholderFor(field);
  if (field.type === 'money') {
    const n = Number(raw);
    return Number.isFinite(n) ? `$${n.toLocaleString('en-US')}` : placeholderFor(field);
  }
  if (Array.isArray(raw)) return raw.length ? raw.join(', ') : placeholderFor(field);
  return String(raw);
}
